import {
  atualizarCupomSchema,
  criarCupomSchema,
  descontoDoCupom,
  formatBRL,
  fromWire,
  localDayKey,
  normalizarCodigoDeCupom,
  PONTOS_BASE_NEUTROS,
  ROTULO_DO_TIPO_DE_DESCONTO,
  type TipoDeDesconto,
  TIPOS_DE_DESCONTO,
} from '@locaveri/shared'
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { LoaderCircle } from 'lucide-react'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api'
import { deCampoDeReaisParaWire, formatarMoedaDoWire, paraCampoDeReais } from '@/lib/dinheiro'
import { ROTAS_DE_CUPONS } from '@/lib/endpoints'
import { aplicarErrosDaApi, aplicarErrosDoZod } from '@/lib/formulario'
import { montarQuery } from '@/lib/query-string'

import { formatarData } from '@/lib/datas'

import { CHAVE_DE_COTACOES, type FiltroDeAtivo, paginaDe } from './api'

/**
 * Cupons de desconto — catálogo de campanha e o formulário que o mantém.
 *
 * A camada de dados mora aqui pelo mesmo motivo explicado em
 * `./dialogo-de-extra`: cupom é catálogo independente do módulo de preço, e
 * `react-refresh` proíbe export que não seja componente dentro de `src/pages`.
 *
 * ---------------------------------------------------------------------------
 * PERCENTUAL NA TELA, PONTOS-BASE NA API — E O BUG DE FATOR 100
 * ---------------------------------------------------------------------------
 * A API guarda o desconto em PONTOS-BASE inteiros: `1500` é 15%. Não é
 * preciosismo — `0,15` não é representável em binário, e um percentual que
 * "quase" fecha vira centavo perdido multiplicado por toda uma campanha. A
 * pessoa, por outro lado, pensa em "15%".
 *
 * A conversão acontece NESTE arquivo, nos dois sentidos, em um lugar só:
 * `percentualParaPontosBase` e `pontosBaseParaCampo`. É o único ponto desta
 * tela onde um fator 100 pode nascer, e por isso a conta é feita sobre os
 * DÍGITOS da string — `0,07 * 100` em ponto flutuante dá 7.000000000000001, e
 * um `Math.round` esquecido aqui viraria uma campanha inteira com o desconto
 * errado.
 *
 * ---------------------------------------------------------------------------
 * O CÓDIGO É NORMALIZADO ANTES DE QUALQUER COISA — E NÃO SE EDITA
 * ---------------------------------------------------------------------------
 * MAIÚSCULAS, sem espaço, via `normalizarCodigoDeCupom` do pacote compartilhado
 * (a mesma função que a API chama). O índice único do banco depende disso: sem
 * a normalização, `bemvindo15`, `BEMVINDO15` e `BEMVINDO 15` seriam três cupons
 * distintos, e quem digitasse com espaço receberia "cupom inválido" para um
 * cupom que existe.
 *
 * Na EDIÇÃO o campo é somente leitura: o código já foi impresso em campanha,
 * e-mail e link. Trocá-lo não corrige nada — desliga o cupom antigo sem ninguém
 * perceber. Quem quer outro código cria outro cupom. `usosRealizados` também
 * não se edita: é contador de fatos.
 */

// ---------------------------------------------------------------------------
// Modelo de leitura
// ---------------------------------------------------------------------------

/**
 * Um cupom como o painel o vê.
 *
 * `valorFixo`, `descontoMaximo` e `valorMinimoDaReserva` são STRING de centavos
 * (invariante nº 1). `vigenteDe`/`vigenteAte` são datas de CALENDÁRIO
 * (`AAAA-MM-DD`), não instantes: "vale até 31/12" não muda de significado em
 * Manaus, e passá-las por `new Date` adiantaria um dia em todo fuso negativo.
 *
 * `usosRealizados` é o contador materializado da tabela, não uma contagem de
 * resgates: a checagem de esgotamento roda em toda cotação, e varrer os
 * resgates a cada uma não escala.
 */
export const cupomSchema = z.object({
  id: z.uuid(),
  codigo: z.string(),
  tipo: z.enum(TIPOS_DE_DESCONTO),
  /** Pontos-base quando `PERCENTUAL` (1500 = 15%). */
  valorEmPontosBase: z.number().int(),
  valorFixo: z.string().nullable(),
  /** Teto do desconto em `PERCENTUAL`. `null` = sem teto. */
  descontoMaximo: z.string().nullable(),
  /** Piso da reserva para o cupom valer. */
  valorMinimoDaReserva: z.string().nullable(),
  vigenteDe: z.string(),
  vigenteAte: z.string().nullable(),
  /** `null` = ilimitado, nunca zero. */
  usosMaximos: z.number().int().nullable(),
  usosPorConta: z.number().int().nullable(),
  usosRealizados: z.number().int().nonnegative(),
  ativo: z.boolean(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type Cupom = z.infer<typeof cupomSchema>

export const paginaDeCuponsSchema = paginaDe(cupomSchema)

export type PaginaDeCupons = z.infer<typeof paginaDeCuponsSchema>

/** Corpo já validado por um schema de `@locaveri/shared`, pronto para o fio. */
export type CorpoDeCupom = Readonly<Record<string, unknown>>

export interface FiltrosDeCupons {
  readonly pagina: number
  readonly tamanho: number
  /** Busca pelo CÓDIGO — normalizada pela API do mesmo jeito que o cadastro. */
  readonly busca: string
  readonly tipo: TipoDeDesconto | ''
  readonly ativo: FiltroDeAtivo
  /** Só os cupons vigentes nesta data de calendário (`AAAA-MM-DD`). */
  readonly vigenteEm: string
  /** `'true'` descarta o que já bateu o teto de usos — o filtro de campanha viva. */
  readonly apenasDisponiveis: FiltroDeAtivo
}

export const CHAVE_DE_CUPONS = 'cupons'

// ---------------------------------------------------------------------------
// Consulta e escrita
// ---------------------------------------------------------------------------

/**
 * Mexer numa campanha muda o ORÇAMENTO que a tela está exibindo. A cotação já
 * emitida não muda (`QuoteSnapshot` congela o desconto linha a linha); o que
 * sai do cache é a que a tela acabou de pedir.
 */
function invalidarCupons(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CUPONS] })
  void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_COTACOES] })
}

export function useCupons(filtros: FiltrosDeCupons): UseQueryResult<PaginaDeCupons> {
  return useQuery({
    queryKey: [CHAVE_DE_CUPONS, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        busca: filtros.busca,
        tipo: filtros.tipo,
        ativo: filtros.ativo,
        vigenteEm: filtros.vigenteEm,
        apenasDisponiveis: filtros.apenasDisponiveis,
      })

      return apiGet(`${ROTAS_DE_CUPONS.lista}${query}`, paginaDeCuponsSchema, { signal })
    },
    placeholderData: (anterior) => anterior,
  })
}

export function useCriarCupom(): UseMutationResult<Cupom, Error, CorpoDeCupom> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeCupom) => apiPost(ROTAS_DE_CUPONS.lista, cupomSchema, dados),
    onSuccess: () => {
      invalidarCupons(queryClient)
    },
  })
}

export interface AtualizacaoDeCupom {
  readonly id: string
  readonly dados: CorpoDeCupom
}

export function useAtualizarCupom(): UseMutationResult<Cupom, Error, AtualizacaoDeCupom> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDeCupom) =>
      apiPatch(ROTAS_DE_CUPONS.porId(id), cupomSchema, dados),
    onSuccess: () => {
      invalidarCupons(queryClient)
    },
  })
}

/**
 * `DELETE` marca `ativo: false`; a linha NÃO é apagada. Apagar levaria junto,
 * em cascata, todos os resgates — o registro de quem ganhou desconto em quais
 * reservas, que é o que o financeiro procura quando o faturamento não bate.
 */
export function useDesligarCupom(): UseMutationResult<Cupom, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => apiDelete(ROTAS_DE_CUPONS.porId(id), cupomSchema),
    onSuccess: () => {
      invalidarCupons(queryClient)
    },
  })
}

// ---------------------------------------------------------------------------
// Conversão de percentual — o único lugar onde o fator 100 pode nascer
//
// PONTOS-BASE SÃO DUAS ESCALAS NESTE MÓDULO, E ELAS NÃO SE MISTURAM
//
//   cupom (aqui):        1500  = 15%    · 10000 = 100% (`PONTOS_BASE_NEUTROS`)
//   multiplicador (regra de preço, `./api`): 12000 = 1,2× · 10000 = 1× (neutro)
//
// O inteiro é o mesmo e o significado não: 12000 num cupom seria 120% de
// desconto — e é por isso que `percentualParaPontosBase` recusa qualquer coisa
// acima de `PONTOS_BASE_NEUTROS` em vez de deixar passar e confiar no CHECK.
//
// `./api` formata a OUTRA escala (`formatarMultiplicador`,
// `formatarPontosBaseComoPercentual`) com divisão em ponto flutuante, o que
// serve à leitura de uma faixa de ocupação e NÃO serve aqui: o que sai destas
// funções volta para dentro de um campo de formulário e precisa ser relido
// pela mesma expressão que o validou. Por isso a conta abaixo é sobre os
// DÍGITOS da string, e não um `Number(texto) * 100`.
// ---------------------------------------------------------------------------

const PONTOS_BASE_POR_PONTO_PERCENTUAL = 100

/**
 * `12`, `12,5` ou `12.34` — até três dígitos inteiros e duas casas.
 *
 * Escrito como ALTERNÂNCIA de dois padrões em vez de um grupo opcional
 * aninhado: `eslint-plugin-security` mede altura de estrela, e silenciar o
 * plugin por regra caso a caso é pior do que escrever a expressão que ele
 * consegue provar segura.
 */
const PERCENTUAL_DIGITADO = /^\d{1,3}$|^\d{1,3}[.,]\d{1,2}$/

/**
 * `"15"` → `1500`; `"12,5"` → `1250`. `null` quando não dá para ler ou quando
 * passa de 100% — e `null` vira erro de campo, nunca um zero silencioso (um
 * cupom com desconto zero é um cupom que o cliente aplica achando que
 * descontou).
 *
 * A conta é feita sobre os DÍGITOS da string, e não com `Number(texto) * 100`,
 * pelo motivo do cabeçalho do arquivo.
 */
function percentualParaPontosBase(texto: string): number | null {
  const limpo = texto.trim().replace('%', '').trim()

  if (!PERCENTUAL_DIGITADO.test(limpo)) {
    return null
  }

  const [inteiro = '0', decimal = ''] = limpo.split(/[.,]/)
  const pontosBase =
    Number(inteiro) * PONTOS_BASE_POR_PONTO_PERCENTUAL + Number(decimal.padEnd(2, '0'))

  return pontosBase > PONTOS_BASE_NEUTROS ? null : pontosBase
}

/**
 * `1500` → `"15"`; `1250` → `"12,5"`. O caminho de volta, para o formulário
 * abrir com o mesmo texto que a pessoa digitou da primeira vez.
 *
 * Quociente e resto em inteiros, e não `pontos / 100` formatado: a divisão
 * devolveria um float e o texto passaria a depender do arredondamento do
 * formatador.
 */
function pontosBaseParaCampo(pontosBase: number): string {
  const inteiro = Math.trunc(pontosBase / PONTOS_BASE_POR_PONTO_PERCENTUAL)
  const centesimos = Math.abs(pontosBase % PONTOS_BASE_POR_PONTO_PERCENTUAL)

  if (centesimos === 0) {
    return String(inteiro)
  }

  // `05` continua `05`; `50` vira `5`. Só o zero à DIREITA é ruído.
  const casas = String(centesimos).padStart(2, '0').replace(/0$/, '')

  return `${String(inteiro)},${casas}`
}

/** `1500` → `15%`. Para leitura em tabela e cartão. */
export function percentualDoCupom(pontosBase: number): string {
  return `${pontosBaseParaCampo(pontosBase)}%`
}

// ---------------------------------------------------------------------------
// Apresentação
// ---------------------------------------------------------------------------

/** Hoje, em `AAAA-MM-DD`, no fuso da OPERAÇÃO — não no do navegador. */
export function hojeNaOperacao(): string {
  return localDayKey(new Date())
}

/**
 * O desconto em uma linha: `15% (teto R$ 200,00)` ou `R$ 150,00`.
 *
 * O teto entra junto porque sem ele o percentual mente: um cupom de 20% numa
 * locação de R$ 12 mil descontaria R$ 2.400, e é o teto que faz a campanha
 * caber no orçamento.
 */
export function descontoEmTexto(cupom: Cupom): string {
  if (cupom.tipo === 'VALOR') {
    return formatarMoedaDoWire(cupom.valorFixo)
  }

  const percentual = percentualDoCupom(cupom.valorEmPontosBase)

  return cupom.descontoMaximo === null
    ? percentual
    : `${percentual} (teto ${formatarMoedaDoWire(cupom.descontoMaximo)})`
}

/** `null` = ilimitado, e nunca zero: `null` aqui é "sem teto", não "acabou". */
export function usosRestantes(cupom: Cupom): number | null {
  return cupom.usosMaximos === null ? null : Math.max(0, cupom.usosMaximos - cupom.usosRealizados)
}

export function usosEmTexto(cupom: Cupom): string {
  return cupom.usosMaximos === null
    ? `${String(cupom.usosRealizados)} de ilimitados`
    : `${String(cupom.usosRealizados)} de ${String(cupom.usosMaximos)}`
}

export const SITUACOES_DO_CUPOM = [
  'DESLIGADO',
  'VENCIDO',
  'AGENDADO',
  'ESGOTADO',
  'VIGENTE',
] as const

export type SituacaoDoCupom = (typeof SITUACOES_DO_CUPOM)[number]

/**
 * A situação REAL de um cupom, na ordem em que o gestor precisa saber dela.
 *
 * `ativo` é um campo; "vale hoje" é uma conta entre quatro campos, e a listagem
 * mostraria "Ativo" para uma campanha que acabou em março se dependesse só do
 * interruptor. A ordem das perguntas não é arbitrária: desligado vem primeiro
 * porque é decisão de alguém (e é o que se desfaz com um clique); vencido e
 * agendado vêm antes de esgotado porque o calendário explica o cupom melhor do
 * que o contador — "acabou o prazo" e "acabaram os usos" pedem ações
 * diferentes.
 *
 * A comparação é lexicográfica entre datas de calendário `AAAA-MM-DD`, que é
 * exatamente como o motor compara os dias. Quem decide de verdade é a API, no
 * fuso da operação; aqui o rótulo é informativo, e por isso `hoje` também vem
 * de `localDayKey` e não do relógio do navegador.
 */
export function situacaoDoCupom(cupom: Cupom, hoje: string): SituacaoDoCupom {
  if (!cupom.ativo) {
    return 'DESLIGADO'
  }
  if (cupom.vigenteAte !== null && cupom.vigenteAte < hoje) {
    return 'VENCIDO'
  }
  if (cupom.vigenteDe > hoje) {
    return 'AGENDADO'
  }
  if (usosRestantes(cupom) === 0) {
    return 'ESGOTADO'
  }
  return 'VIGENTE'
}

/** Base da prévia: um fim de semana de blindado, na ordem de grandeza real. */
const BASE_DA_PREVIA = '200000'

/**
 * Quanto este cupom desconta num orçamento de referência, pela função do MOTOR.
 *
 * `descontoDoCupom` é a mesma função que a cotação chama — é ela que aplica o
 * teto e que nunca deixa o desconto passar da base. Sem esta linha, o teto é um
 * campo que ninguém entende até a primeira reclamação de cliente.
 *
 * `null` enquanto o que foi digitado não formar um cupom legível.
 */
function previaDoDesconto(
  tipo: TipoDeDesconto,
  pontosBase: number | null,
  valorFixoWire: string | null,
  descontoMaximoWire: string | null,
): string | null {
  if (tipo === 'PERCENTUAL' ? pontosBase === null : valorFixoWire === null) {
    return null
  }

  try {
    return formatBRL(
      descontoDoCupom(
        {
          id: 'previa',
          codigo: 'PREVIA',
          tipo,
          valorEmPontosBase: pontosBase ?? 0,
          valorFixo: valorFixoWire === null ? null : fromWire(valorFixoWire),
          descontoMaximo: descontoMaximoWire === null ? null : fromWire(descontoMaximoWire),
        },
        fromWire(BASE_DA_PREVIA),
      ),
    )
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Formulário
// ---------------------------------------------------------------------------

/**
 * Os campos cujo nome COINCIDE com o caminho do schema. `percentual` fica de
 * fora de propósito: no contrato ele é `valorEmPontosBase`, e um erro do Zod
 * naquele caminho cai no alerta do formulário em vez de num campo errado. Na
 * prática ele quase não acontece — o `validate` do próprio campo recusa antes.
 */
const CAMPOS = [
  'codigo',
  'tipo',
  'valorFixo',
  'descontoMaximo',
  'valorMinimoDaReserva',
  'vigenteDe',
  'vigenteAte',
  'usosMaximos',
  'usosPorConta',
  'ativo',
] as const

interface FormularioDeCupom {
  /** Sempre em MAIÚSCULAS, sem espaço — normalizado a cada tecla. */
  codigo: string
  tipo: TipoDeDesconto
  /** PERCENTUAL digitado (`"15"`), convertido para pontos-base no envio. */
  percentual: string
  /** Reais digitados, quando o cupom é de valor. */
  valorFixo: string
  descontoMaximo: string
  valorMinimoDaReserva: string
  /** `AAAA-MM-DD`. Data de calendário, sem hora e sem fuso. */
  vigenteDe: string
  vigenteAte: string
  usosMaximos: string
  usosPorConta: string
  /** Não é string: `z.boolean()` não faz coerção e `"false"` seria verdadeiro. */
  ativo: boolean
}

const PERCENTUAL_INVALIDO = 'percentual inválido — use de 0 a 100, com até duas casas (ex.: 12,5)'

/**
 * Zero é o caso perigoso, e por isso ganha mensagem própria: um cupom
 * percentual de 0% passa por qualquer validação de formato e não desconta nada
 * — o cliente aplica achando que descontou. O contrato compartilhado também o
 * recusa, mas com o erro apontando para `tipo`, que é onde ninguém procuraria.
 */
function validarPercentual(valor: string): true | string {
  const pontos = percentualParaPontosBase(valor)

  if (pontos === null) {
    return PERCENTUAL_INVALIDO
  }

  return pontos > 0 || 'o percentual precisa ser maior que zero'
}

function campoDeInteiro(valor: number | null | undefined): string {
  return valor === null || valor === undefined ? '' : String(valor)
}

/** Texto vazio vira `null` (= ilimitado / sem teto), nunca zero. */
function textoOuNulo(texto: string): string | null {
  const limpo = texto.trim()

  return limpo.length === 0 ? null : limpo
}

function valoresIniciais(cupom: Cupom | undefined): FormularioDeCupom {
  return {
    codigo: cupom?.codigo ?? '',
    tipo: cupom?.tipo ?? 'PERCENTUAL',
    percentual: cupom === undefined ? '' : pontosBaseParaCampo(cupom.valorEmPontosBase),
    valorFixo: paraCampoDeReais(cupom?.valorFixo),
    descontoMaximo: paraCampoDeReais(cupom?.descontoMaximo),
    valorMinimoDaReserva: paraCampoDeReais(cupom?.valorMinimoDaReserva),
    // `<input type="date">` fica MUDO com um ISO completo — daí o corte.
    vigenteDe: (cupom?.vigenteDe ?? hojeNaOperacao()).slice(0, 10),
    vigenteAte: (cupom?.vigenteAte ?? '').slice(0, 10),
    usosMaximos: campoDeInteiro(cupom?.usosMaximos),
    usosPorConta: campoDeInteiro(cupom?.usosPorConta),
    ativo: cupom?.ativo ?? true,
  }
}

export interface DialogoDeCupomProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** `undefined` cria; preenchido edita — e aí o código é imutável. */
  readonly cupom?: Cupom | undefined
}

export function DialogoDeCupom({ aberto, aoFechar, cupom }: DialogoDeCupomProps): ReactElement {
  const editando = cupom !== undefined
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const criar = useCriarCupom()
  const atualizar = useAtualizarCupom()

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    reset,
    watch,
    formState: { errors, isSubmitted },
  } = useForm<FormularioDeCupom>({ defaultValues: valoresIniciais(undefined) })

  // O diálogo é montado uma vez e reaproveitado: sem este efeito, abrir
  // "editar" depois de "novo cupom" mostraria os valores do formulário anterior.
  useEffect(() => {
    if (aberto) {
      setErroGeral(null)
      reset(valoresIniciais(cupom))
    }
  }, [aberto, cupom, reset])

  const codigo = watch('codigo')
  const tipo = watch('tipo')
  const percentualDigitado = watch('percentual')
  const valorFixoDigitado = watch('valorFixo')
  const tetoDigitado = watch('descontoMaximo')

  const percentual = tipo === 'PERCENTUAL'
  const ocupado = criar.isPending || atualizar.isPending

  const previa = useMemo(
    () =>
      previaDoDesconto(
        tipo,
        percentualParaPontosBase(percentualDigitado),
        percentual ? null : deCampoDeReaisParaWire(valorFixoDigitado),
        percentual ? deCampoDeReaisParaWire(tetoDigitado) : null,
      ),
    [tipo, percentual, percentualDigitado, valorFixoDigitado, tetoDigitado],
  )

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    const pontosBase = percentual ? percentualParaPontosBase(valores.percentual) : 0

    if (pontosBase === null) {
      setError('percentual', { type: 'validate', message: PERCENTUAL_INVALIDO })
      return
    }

    const valorFixoWire = percentual ? null : deCampoDeReaisParaWire(valores.valorFixo)

    if (!percentual && valorFixoWire === null) {
      setError('valorFixo', { type: 'validate', message: 'informe o valor do desconto' })
      return
    }

    // Teto só faz sentido em cupom percentual; num cupom de valor ele seria um
    // campo mentiroso gravado no banco. Piso da reserva vale para os dois.
    const tetoWire = percentual ? deCampoDeReaisParaWire(valores.descontoMaximo) : null
    const pisoWire = deCampoDeReaisParaWire(valores.valorMinimoDaReserva)

    const comuns = {
      tipo: valores.tipo,
      valorEmPontosBase: pontosBase,
      valorFixo: valorFixoWire,
      descontoMaximo: tetoWire,
      valorMinimoDaReserva: pisoWire,
      vigenteDe: valores.vigenteDe,
      vigenteAte: textoOuNulo(valores.vigenteAte),
      usosMaximos: textoOuNulo(valores.usosMaximos),
      usosPorConta: textoOuNulo(valores.usosPorConta),
      ativo: valores.ativo,
    }

    if (cupom === undefined) {
      // `normalizarCodigoDeCupom` é a MESMA função que a API chama. O campo já
      // normaliza a cada tecla; repetir aqui cobre o preenchimento automático do
      // navegador, que não passa pelo `onChange`.
      const entrada = { codigo: normalizarCodigoDeCupom(valores.codigo), ...comuns }
      const validado = criarCupomSchema.safeParse(entrada)

      if (!validado.success) {
        setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
        return
      }

      // `entrada`, e não `validado.data`: o schema transforma dinheiro em
      // `bigint`, e `JSON.stringify` de um `bigint` LANÇA.
      criar.mutate(entrada, {
        onSuccess: (salvo) => {
          toast.success(`Cupom ${salvo.codigo} criado`, {
            description: `Desconto de ${descontoEmTexto(salvo)}, a partir de ${formatarData(salvo.vigenteDe)}.`,
          })
          aoFechar()
        },
        onError: (causa: unknown) => {
          setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
        },
      })
      return
    }

    // O código NÃO viaja na edição: `atualizarCupomSchema` nem o aceita.
    const validado = atualizarCupomSchema.safeParse(comuns)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    atualizar.mutate(
      { id: cupom.id, dados: comuns },
      {
        onSuccess: (salvo) => {
          toast.success(`Cupom ${salvo.codigo} atualizado`, {
            description: salvo.ativo
              ? `Desconto de ${descontoEmTexto(salvo)}.`
              : 'O cupom ficou desligado — o cliente recebe “cupom inválido”.',
          })
          aoFechar()
        },
        onError: (causa: unknown) => {
          setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
        },
      },
    )
  })

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editando ? `Editar ${cupom.codigo}` : 'Novo cupom'}</DialogTitle>
          <DialogDescription>
            {editando
              ? 'O código não se edita: ele já foi impresso em campanha, e-mail e link. Para outro código, crie outro cupom.'
              : 'O código é gravado em maiúsculas e sem espaço — é assim que o cliente o digita e é assim que ele é procurado.'}
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(evento) => {
            void aoEnviar(evento)
          }}
          className="space-y-4"
        >
          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Código"
              obrigatorio
              erro={errors.codigo?.message}
              dica="De 3 a 32 caracteres, entre letras, números, hífen e sublinhado."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('codigo', { required: 'informe o código' })}
                  // A máscara vem DEPOIS do `register`, senão o `onChange` dele
                  // vence e o texto some em minúsculas.
                  value={codigo}
                  onChange={(evento) => {
                    // `shouldValidate` só depois do primeiro envio: validar a
                    // cada tecla acusaria "código curto demais" no segundo
                    // caractere de todo cupom.
                    setValue('codigo', normalizarCodigoDeCupom(evento.target.value), {
                      shouldValidate: isSubmitted,
                    })
                  }}
                  maxLength={32}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="BEMVINDO15"
                  className="tabular-money uppercase"
                  disabled={ocupado || editando}
                  readOnly={editando}
                />
              )}
            </Campo>

            <Campo
              rotulo="Tipo de desconto"
              obrigatorio
              erro={errors.tipo?.message}
              dica="Percentual desconta uma fatia; valor fixo desconta um número."
            >
              {(campo) => (
                <Select {...campo} {...register('tipo')} disabled={ocupado}>
                  {TIPOS_DE_DESCONTO.map((item) => (
                    <option key={item} value={item}>
                      {ROTULO_DO_TIPO_DE_DESCONTO[item]}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>

            {percentual ? (
              <>
                <Campo
                  rotulo="Percentual"
                  obrigatorio
                  erro={errors.percentual?.message}
                  dica="Em % — a API guarda em pontos-base (15 vira 1500), e a conversão é feita aqui."
                >
                  {(campo) => (
                    <Input
                      {...campo}
                      {...register('percentual', { validate: validarPercentual })}
                      inputMode="decimal"
                      placeholder="15"
                      className="tabular-money"
                      disabled={ocupado}
                    />
                  )}
                </Campo>

                <Campo
                  rotulo="Teto do desconto"
                  erro={errors.descontoMaximo?.message}
                  dica="Em reais. Vazio = sem teto — e aí 20% de uma locação longa vira um número grande."
                >
                  {(campo) => (
                    <Input
                      {...campo}
                      {...register('descontoMaximo', {
                        validate: (valor: string) =>
                          valor.trim().length === 0 ||
                          deCampoDeReaisParaWire(valor) !== null ||
                          'valor inválido — use o formato 200,00',
                      })}
                      inputMode="decimal"
                      placeholder="sem teto"
                      className="tabular-money"
                      disabled={ocupado}
                    />
                  )}
                </Campo>
              </>
            ) : (
              <Campo
                rotulo="Valor do desconto"
                obrigatorio
                erro={errors.valorFixo?.message}
                dica="Em reais. Ex.: 150,00"
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('valorFixo', {
                      validate: (valor: string) =>
                        valor.trim().length === 0
                          ? 'informe o valor do desconto'
                          : deCampoDeReaisParaWire(valor) !== null ||
                            'valor inválido — use o formato 150,00',
                    })}
                    inputMode="decimal"
                    placeholder="0,00"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>
            )}

            <Campo
              rotulo="Valor mínimo da reserva"
              erro={errors.valorMinimoDaReserva?.message}
              dica="Piso para o cupom valer. Vazio = vale em qualquer reserva."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('valorMinimoDaReserva', {
                    validate: (valor: string) =>
                      valor.trim().length === 0 ||
                      deCampoDeReaisParaWire(valor) !== null ||
                      'valor inválido — use o formato 500,00',
                  })}
                  inputMode="decimal"
                  placeholder="sem mínimo"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>
          </div>

          {/* A prévia chama a MESMA função do motor que a cotação chama — é ela
              que aplica o teto e que nunca deixa o desconto passar da base. */}
          {previa === null ? null : (
            <Alert variant="info">
              <AlertTitle>Quanto este cupom desconta</AlertTitle>
              <AlertDescription>
                Num orçamento de{' '}
                <span className="tabular-money">{formatarMoedaDoWire(BASE_DA_PREVIA)}</span>, o
                desconto é de <span className="tabular-money">{previa}</span>.
                {percentual && tetoDigitado.trim().length > 0
                  ? ' O teto já está aplicado neste número.'
                  : ''}
              </AlertDescription>
            </Alert>
          )}

          <fieldset className="space-y-4" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">Vigência</legend>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Vigente de" obrigatorio erro={errors.vigenteDe?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('vigenteDe', { required: 'informe o início da vigência' })}
                    type="date"
                    className="tabular-money"
                  />
                )}
              </Campo>

              <Campo
                rotulo="Vigente até"
                erro={errors.vigenteAte?.message}
                dica="Vazio = sem prazo. O último dia vale inteiro, no fuso da operação."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('vigenteAte')}
                    type="date"
                    className="tabular-money"
                  />
                )}
              </Campo>
            </div>
          </fieldset>

          <fieldset className="space-y-4" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">Limites de uso</legend>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Usos máximos"
                erro={errors.usosMaximos?.message}
                dica="No total da campanha. Vazio = ilimitado."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('usosMaximos')}
                    type="number"
                    min={1}
                    inputMode="numeric"
                    placeholder="ilimitado"
                    className="tabular-money"
                  />
                )}
              </Campo>

              <Campo
                rotulo="Usos por conta"
                erro={errors.usosPorConta?.message}
                dica="Quantas vezes o MESMO cliente pode usar. Vazio = ilimitado."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('usosPorConta')}
                    type="number"
                    min={1}
                    inputMode="numeric"
                    placeholder="ilimitado"
                    className="tabular-money"
                  />
                )}
              </Campo>
            </div>

            {editando ? (
              <p className="text-xs tabular-money text-fg-muted">
                Já resgatado {cupom.usosRealizados} {cupom.usosRealizados === 1 ? 'vez' : 'vezes'}
                {cupom.usosMaximos === null
                  ? ', sem teto'
                  : ` de ${String(cupom.usosMaximos)} — ${String(usosRestantes(cupom) ?? 0)} restantes`}
                . O contador não se edita: é registro de fato, e baixá-lo devolveria desconto a quem
                já usou.
              </p>
            ) : null}

            <label
              className="flex cursor-pointer items-start gap-2 rounded-lg border border-border
                bg-surface p-3 text-sm"
            >
              <Checkbox {...register('ativo')} className="mt-0.5" />
              <span className="min-w-0">
                <span className="block font-medium text-foreground">Campanha no ar</span>
                <span className="block text-xs text-fg-secondary">
                  Desmarcado, o cliente recebe “cupom inválido” mesmo dentro da vigência. Os
                  resgates já registrados continuam onde estão.
                </span>
              </span>
            </label>
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
              Cancelar
            </Button>
            <Button type="submit" disabled={ocupado}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              {editando ? 'Salvar alterações' : 'Criar cupom'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
